import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Space } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  useConsumables,
  useDeleteConsumable,
} from '@/core/domain/operational/Consumables/Consumables.hooks';
import {
  Consumable,
  ListConsumablesQuery,
} from '@/core/domain/operational/Consumables/Consumables.types';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import ConsumablesExtraFilters from '@/components/Operational/Inventory/Consumables/ConsumablesExtraFilters';
import ConsumableReportModal from '@/components/Operational/Inventory/Consumables/ConsumableReportModal';
import { useDebounce, useDefaultDeleteModal } from '@/hooks';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

const ConsumablesPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const { handleDelete } = useDefaultDeleteModal('consumables');

  const [queryParams, setQueryParams] = useState<ListConsumablesQuery>({
    active: true,
    order: 'ASC',
    ...convertParams<ListConsumablesQuery>({
      order: 'string',
      active: 'boolean',
      barCode: 'string',
      manufacturingCode: 'string',
      search: 'string',
      sku: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    useConsumables(queryDebounce);

  usePersistSearchParams(queryDebounce);

  const consumables = flatInfiniteData(data);

  const [modalReportVisible, setModalReportVisible] = useState(false);

  function toggleReportModal() {
    setModalReportVisible(!modalReportVisible);
  }

  const deleteMutation = useDeleteConsumable();

  const columns: ColumnsType<Consumable> = [
    {
      title: (
        <FormattedMessage id="consumables.list.header.manufacturingCode" />
      ),
      dataIndex: 'manufacturingCode',
      key: 'manufacturingCode',
      render: (value: string) => value || '-',
      width: 200,
      ellipsis: true,
    },
    {
      title: <FormattedMessage id="consumables.list.header.name" />,
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 300,
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      key: 'actions',
      width: 100,
      ellipsis: true,
      fixed: 'right',
      align: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/operational/inventory/consumables/${row.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={!hasPermissions([Authorities.REGISTER_CONSUMABLE_DELETE])}
            onClick={() =>
              handleDelete(
                () => deleteMutation.mutateAsync(row.id || 0),
                () => refetch(),
              )
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        persistSearchParams={false}
        extra={
          <SpaceForButton>
            <Button
              disabled={
                !hasPermissions([Authorities.REGISTER_CONSUMABLE_REPORTS])
              }
              block
              type="primary"
              onClick={() => toggleReportModal()}
            >
              <FormattedMessage id="commons.page.button.generateReport" />
            </Button>

            <Button
              disabled={!hasPermissions([Authorities.REGISTER_CONSUMABLE_ADD])}
              block
              type="primary"
            >
              <Link to="/app/operational/inventory/consumables/create">
                <FormattedMessage id="consumables.pageHeader.button.newConsumable" />
              </Link>
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                block
                type="primary"
                onClick={() => setQueryParams({ order: 'ASC', active: true })}
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <ConsumablesExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />
        <InfiniteTable<Consumable>
          columns={columns}
          dataSource={consumables}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <ConsumableReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default ConsumablesPage;
